// Conversation-domain slice: chatStatus state and the core send/start/cancel
// turn drivers (startChat, sendMessage, sendTeamMessage, sendPublicMessage,
// cancelSession, stopManagedRun). The message-mutation primitives live in the
// messages slice; the human-in-the-loop drivers (ask_human / credential /
// guide / takeover) live in the human-interaction slice.
//
// Composed into useAgentStore via slice composition (single create()).
// Cross-slice calls go through get(), which resolves to the full store.

import {
  send_request,
} from "../../utils/Request";
import {
  postTeamMessage,
} from "../../api/teamsApi";
import {
  log as _log,
} from "../../utils/ChatStreamLogger";
import {
  stopManagedRun as stopManagedRunRequest,
} from "./ManagedRunPolicy/managedRunApi";
import {
  buildOptimisticSessionListRow,
  upsertSessionListRow,
} from "./sessionListOptimistic";
import {
  extractApiStatusCode,
  resolveApiErrorMessage,
  _extractBlockedIngressPayload,
  _buildBlockedIngressDetailPatch,
  _bumpAgentLastActivityPatch,
  _generateOptimisticId,
  _getLocalUserDisplayName,
} from "./agentStoreUtils";
import {
  _markChatStatusReason,
} from "./agentStreamLifecycle";
import {
  reduceChatLifecycle,
  INITIAL_LIFECYCLE,
} from "./conversation/lifecycle/chatLifecycleReducer";
import useChatStreamStore from "./useChatStreamStore";
import usePublicChatStore from "./usePublicChatStore";

export function createConversationSlice(set, get) {
  return {
  _startingChatContext: null,
  chatStatus: 'ready',
  // Sequence-gated lifecycle state — the single owner of the chatStatus
  // decision (see conversation/lifecycle/chatLifecycleReducer.js). `chatStatus`
  // is kept as a top-level field mirrored from `lifecycle.status` by
  // _applyLifecycle, the ONLY writer of chatStatus, so the ~50 chatStatus
  // consumers are unchanged.
  lifecycle: INITIAL_LIFECYCLE,
  // Diagnostic breadcrumb for the most recent chatStatus transition, stamped by
  // _markChatStatusReason via the sink wired in useAgentStore.js. Read by the
  // transition-logging subscription; not consumed by the UI.
  chatStatusReason: null,
  _isPublicSession: false,

  // The single funnel for every chatStatus transition. Reduces one input
  // against the current lifecycle state, mirrors the derived status into
  // chatStatus, and returns whether the input was accepted so callers can gate
  // side effects (e.g. a REST _replaceMessages only runs when its input was
  // accepted — §4.5). Rejected inputs (stale seq, superseded epoch, lagging
  // REST, cross-session) leave state untouched.
  _applyLifecycle: (input) => {
    const prev = get().lifecycle;
    const { next, accepted, reason } = reduceChatLifecycle(prev, input);
    if (next !== prev) {
      _markChatStatusReason(reason);
      set({ lifecycle: next, chatStatus: next.status });
    }
    return accepted;
  },

  // Public reset funnel for external consumer writers (session select / new chat /
  // agent change / clear / URL sync). Abandons the current turn and parks the
  // lifecycle idle for `sessionId` (§4.6a): bumps the epoch so any in-flight
  // REST/SSE for the old turn is rejected by the epoch/session gates, and — when a
  // session id is given — establishes lifecycle.sessionId so the newly selected
  // session's events pass the §4.1 session gate. The ONLY chatStatus writer is
  // still _applyLifecycle.
  resetLifecycle: (sessionId = null) => {
    get()._applyLifecycle({ kind: 'reset', sessionId: sessionId ?? null });
  },

  startChat: async (
    agentId,
    message,
    imageUrls,
    {
      projectId,
      projectName,
      tableId,
      tableName,
      boardId,
      boardName,
      teamId,
      teamName,
      docId,
      docTitle,
      artifactDefinitionId,
      artifactInstanceId,
      artifactComponentId,
      artifactTitle,
      fileScope,
      targetAgentId,
      targetAgentName,
      workspaceId,
      workspaceName,
      fileId,
      fileName,
      customFunctionId,
      customFunctionName,
      customFunctionDisplayName,
      customFunctionDraft,
    } = {}
  ) => {
    const optimisticId = _generateOptimisticId();
    const clientRequestId = crypto.randomUUID();
    const senderDisplayName = _getLocalUserDisplayName();
    const optimisticMsg = {
      messageId: optimisticId,
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      imageUrls: imageUrls || undefined,
      senderDisplayName,
      _optimistic: true,
      clientRequestId,
    };
    // New chat: open a fresh turn (no session id yet — adopted from the create
    // response / first SSE event). The reducer is the sole chatStatus writer.
    get()._applyLifecycle({ kind: 'optimistic', sessionId: null, action: 'startChat' });
    set({
      sendError: null,
      _startingChatContext: {
        agentId,
        message,
        senderDisplayName: senderDisplayName || "You",
        clientRequestId,
      },
    });
    get()._replaceMessages(null, [optimisticMsg], {
      chatSessionId: null,
      messages: [optimisticMsg],
      status: "IN_PROGRESS",
    });
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payload = { message, userTimezone, clientRequestId };
      if (imageUrls && imageUrls.length > 0) payload.imageUrls = imageUrls;
      if (projectId) payload.projectId = projectId;
      if (projectName) payload.projectName = projectName;
      if (tableId) payload.tableId = tableId;
      if (tableName) payload.tableName = tableName;
      if (boardId) payload.boardId = boardId;
      if (boardName) payload.boardName = boardName;
      if (teamId) payload.teamId = teamId;
      if (teamName) payload.teamName = teamName;
      if (docId) payload.docId = docId;
      if (docTitle) payload.docTitle = docTitle;
      if (artifactDefinitionId) payload.artifactDefinitionId = artifactDefinitionId;
      if (artifactInstanceId) payload.artifactInstanceId = artifactInstanceId;
      if (artifactComponentId) payload.artifactComponentId = artifactComponentId;
      if (artifactTitle) payload.artifactTitle = artifactTitle;
      if (fileScope) payload.fileScope = fileScope;
      if (targetAgentId) payload.targetAgentId = targetAgentId;
      if (targetAgentName) payload.targetAgentName = targetAgentName;
      if (workspaceId) payload.workspaceId = workspaceId;
      if (workspaceName) payload.workspaceName = workspaceName;
      if (fileId) payload.fileId = fileId;
      if (fileName) payload.fileName = fileName;
      if (customFunctionId) payload.customFunctionId = customFunctionId;
      if (customFunctionName) payload.customFunctionName = customFunctionName;
      if (customFunctionDisplayName) {
        payload.customFunctionDisplayName = customFunctionDisplayName;
      }
      if (typeof customFunctionDraft === "boolean") {
        payload.customFunctionDraft = customFunctionDraft;
      }
      const response = await send_request(
        `project-service/agent/${agentId}/chat`,
        payload,
        {},
        "POST"
      );
      const data = response?.data;
      if (data?.chatSessionId) {
        const optimisticListRow = buildOptimisticSessionListRow({
          chatSessionId: data.chatSessionId,
          message,
          senderDisplayName,
        });
        set((s) => ({
          sessionDetail: {
            ...s.sessionDetail,
            chatSessionId: data.chatSessionId,
            status: "IN_PROGRESS",
          },
          ...(_bumpAgentLastActivityPatch(s, agentId) || {}),
          _startingChatContext: null,
          sessions: upsertSessionListRow(s.sessions, optimisticListRow),
        }));
        get().subscribeToSession(data.chatSessionId, { reconnectWhileSubscribed: true, forceReconnect: true });
        get().fetchSessions(agentId, { silent: true });
      }
      return data;
    } catch (e) {
      get()._applyLifecycle({ kind: 'optimistic', sessionId: null, action: 'error' });
      set({
        sendError: e.message || "Failed to start chat",
        sessionDetail: null,
        _startingChatContext: null,
      });
      return null;
    }
  },

  // Render a team conversation optimistically the instant the user sends from the
  // welcome screen, before the parent session exists. Mirrors startChat's optimistic
  // setup so the user's message + "working" indicator appear immediately instead of
  // the welcome screen sitting frozen during session creation. The optimistic user
  // message reconciles (by role+content) once the projected message streams in.
  startTeamChatOptimistic: (message) => {
    const optimisticMsg = {
      messageId: _generateOptimisticId(),
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      senderDisplayName: _getLocalUserDisplayName(),
      _optimistic: true,
    };
    // Team chat opener with no parent session id yet (created server-side); the
    // id is adopted by adoptTeamSessionId + the first SSE event of the new turn.
    get()._applyLifecycle({ kind: 'optimistic', sessionId: null, action: 'startChat' });
    set({
      sendError: null,
      sessionDetail: { chatSessionId: null, status: "IN_PROGRESS", messages: [optimisticMsg] },
      _teamActivityByRun: {},
    });
  },

  // Adopt the real parent session id onto the optimistic detail in place, so the
  // subsequent requestedSessionId-driven fetch treats it as the same session and
  // preserves the optimistic message instead of dropping it as a session switch.
  adoptTeamSessionId: (chatSessionId) => {
    set((s) =>
      s.sessionDetail
        ? { sessionDetail: { ...s.sessionDetail, chatSessionId } }
        : {}
    );
  },

  sendMessage: async (chatSessionId, agentId, message, imageUrls) => {
    _log("Send", `sendMessage: session=${chatSessionId} chatStatus=${get().chatStatus} subscribedSession=${useChatStreamStore.getState().getSubscribedSessionId()}`);
    const optimisticId = _generateOptimisticId();
    const clientRequestId = crypto.randomUUID();
    const senderDisplayName = _getLocalUserDisplayName();
    get()._applyLifecycle({ kind: 'optimistic', sessionId: chatSessionId, action: 'send' });
    set((s) => ({
      sendError: null,
      sessionDetail:
        s.sessionDetail?.chatSessionId === chatSessionId
          ? { ...s.sessionDetail, status: "IN_PROGRESS" }
          : s.sessionDetail,
    }));
    get()._upsertMessage(chatSessionId, {
      messageId: optimisticId,
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      imageUrls: imageUrls || undefined,
      senderDisplayName,
      _optimistic: true,
      clientRequestId,
    });
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const payload = { agentId, message, userTimezone, clientRequestId };
      if (imageUrls && imageUrls.length > 0) {
        payload.imageUrls = imageUrls;
      }
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/message`,
        payload,
        {},
        "POST"
      );
      const blockedPayload = _extractBlockedIngressPayload(response);
      if (blockedPayload) {
        get().disconnectStream();
        get()._removeMessage(chatSessionId, optimisticId);
        get()._applyLifecycle({ kind: 'optimistic', sessionId: chatSessionId, action: 'clearError' });
        set((s) => ({
          error: null,
          sessionDetail: _buildBlockedIngressDetailPatch(s.sessionDetail, chatSessionId, blockedPayload),
        }));
        get().refreshSessionInList(chatSessionId);
        return { chatSessionId, blocked: true };
      }
      const statusCode = extractApiStatusCode(response);
      if (statusCode >= 400) {
        throw new Error(
          resolveApiErrorMessage(response, "Failed to send message")
        );
      }
      get().subscribeToSession(chatSessionId, { reconnectWhileSubscribed: true });
      if (agentId) {
        get().bumpAgentLastActivity(agentId);
      }
      return { chatSessionId };
    } catch (e) {
      get().disconnectStream();
      const errorMsg = resolveApiErrorMessage(e, "Failed to send message");
      get()._removeMessage(chatSessionId, optimisticId);
      get()._applyLifecycle({ kind: 'optimistic', sessionId: chatSessionId, action: 'error' });
      set({ sendError: errorMsg });
      return null;
    }
  },

  // Single source of truth for follow-up messages to an existing team conversation.
  // Mirrors sendMessage's optimistic-message + lifecycle 'send' open + subscribe
  // ordering. This ordering is load-bearing: a message sent after the previous run
  // has COMPLETED must (a) appear instantly and (b) re-open the session SSE so the
  // orchestrator's streamed reply is rendered live. subscribeToSession's
  // isSessionTerminal() guard short-circuits the reconnect while the session is
  // terminal and the chat is idle, so opening a fresh turn through _applyLifecycle
  // (and moving the optimistic detail off the terminal status) BEFORE subscribing
  // is what lets the stream reconnect at all — without it the message only appears
  // after a refresh re-fetches the transcript. Posting to the team endpoint is the
  // only team-specific part; replies arrive over the shared session SSE as usual.
  // The optimistic user bubble reconciles by role+content (team POST carries no
  // clientRequestId) once the projected message streams in via _upsertMessage.
  sendTeamMessage: async (chatSessionId, teamId, message) => {
    _log("Send", `sendTeamMessage: session=${chatSessionId} chatStatus=${get().chatStatus} subscribedSession=${useChatStreamStore.getState().getSubscribedSessionId()}`);
    if (!chatSessionId || !message) return null;
    const optimisticId = _generateOptimisticId();
    const senderDisplayName = _getLocalUserDisplayName();
    const prevStatus =
      get().sessionDetail?.chatSessionId === chatSessionId
        ? get().sessionDetail?.status
        : undefined;
    get()._applyLifecycle({ kind: 'optimistic', sessionId: chatSessionId, action: 'send' });
    set((s) => ({
      sendError: null,
      sessionDetail:
        s.sessionDetail?.chatSessionId === chatSessionId
          ? { ...s.sessionDetail, status: "IN_PROGRESS" }
          : s.sessionDetail,
    }));
    get()._upsertMessage(chatSessionId, {
      messageId: optimisticId,
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      senderDisplayName,
      _optimistic: true,
    });
    try {
      const res = await postTeamMessage(chatSessionId, message);
      const statusCode = extractApiStatusCode(res);
      if (statusCode < 200 || statusCode >= 300) {
        throw new Error(resolveApiErrorMessage(res, "Failed to send message"));
      }
      get().subscribeToSession(chatSessionId, { reconnectWhileSubscribed: true });
      if (teamId) {
        get().bumpTeamLastActivity(teamId);
      }
      return { chatSessionId };
    } catch (e) {
      const errorMsg = resolveApiErrorMessage(e, "Failed to send message");
      get()._removeMessage(chatSessionId, optimisticId);
      get()._applyLifecycle({ kind: 'optimistic', sessionId: chatSessionId, action: 'error' });
      set((s) => ({
        sendError: errorMsg,
        sessionDetail:
          s.sessionDetail?.chatSessionId === chatSessionId &&
          prevStatus !== undefined
            ? { ...s.sessionDetail, status: prevStatus }
            : s.sessionDetail,
      }));
      return null;
    }
  },

  /**
   * Records the human's answer to an `ask_human` question and re-subscribes so the resumed agent
   * loop streams back over the existing SSE (the original tool part flips to its answered state and
   * a status_changed event unlocks the composer). Mirrors {@link sendMessage}'s subscribe-on-success
   * pattern. Returns true on success, false on failure (the inline card surfaces the error).
   */
  sendPublicMessage: async (chatSessionId, publicSessionToken, agentId, message, imageUrls, senderDisplayName) => {
    const optimisticId = _generateOptimisticId();
    const clientRequestId = crypto.randomUUID();
    get()._applyLifecycle({ kind: 'optimistic', sessionId: chatSessionId, action: 'send' });
    set((s) => ({
      sendError: null,
      sessionDetail:
        s.sessionDetail?.chatSessionId === chatSessionId
          ? { ...s.sessionDetail, status: "IN_PROGRESS" }
          : s.sessionDetail,
    }));
    get()._upsertMessage(chatSessionId, {
      messageId: optimisticId,
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
      imageUrls: imageUrls || undefined,
      senderDisplayName,
      _optimistic: true,
      clientRequestId,
    });
    try {
      const payload = { agentId, message, clientRequestId };
      if (imageUrls && imageUrls.length > 0) payload.imageUrls = imageUrls;
      await usePublicChatStore.getState().sendPublicChatMessage(publicSessionToken, payload);
      get().subscribeToSession(chatSessionId, { publicSessionToken, reconnectWhileSubscribed: true, forceReconnect: true });
      return { chatSessionId };
    } catch (e) {
      get()._removeMessage(chatSessionId, optimisticId);
      const blockedPayload = _extractBlockedIngressPayload(e);
      if (blockedPayload) {
        get()._applyLifecycle({ kind: 'optimistic', sessionId: chatSessionId, action: 'clearError' });
        set((s) => ({
          sessionDetail: _buildBlockedIngressDetailPatch(s.sessionDetail, chatSessionId, blockedPayload),
        }));
        return { chatSessionId, blocked: true };
      }
      get()._applyLifecycle({ kind: 'optimistic', sessionId: chatSessionId, action: 'error' });
      set({ sendError: resolveApiErrorMessage(e, "Failed to send message") });
      return null;
    }
  },

  cancelSession: async (chatSessionId) => {
    // Enter a 'cancelling' state and keep the composer locked. Do NOT optimistically flip to
    // 'ready' on the cancel HTTP 200 — the backend may still be unwinding the in-flight run, and
    // re-enabling the composer here lets the user send a new message that races a not-yet-stopped
    // run. The authoritative session_complete SSE event drives the transition to 'ready'.
    get()._applyLifecycle({ kind: 'optimistic', sessionId: chatSessionId, action: 'cancel' });
    set((s) => {
      if (s.sessionDetail?.chatSessionId === chatSessionId) {
        return { sessionDetail: { ...s.sessionDetail, status: "CANCELLING" } };
      }
      return {};
    });
    try {
      const response = await send_request(
        `project-service/agent/session/${chatSessionId}/cancel`,
        {},
        {},
        "POST"
      );
      // send_request resolves (does not throw) for most failures — it returns the error object.
      // Detect a non-success status here and route it through the catch, otherwise a failed cancel
      // would leave the UI stuck in 'cancelling' until the 10s safety timeout with no error shown.
      const statusCode = extractApiStatusCode(response);
      if (statusCode === 0 || statusCode >= 400) {
        throw new Error(resolveApiErrorMessage(response, "Failed to cancel session"));
      }
    } catch (e) {
      // Cancel is a chat-flow action, so surface the failure via sendError (the composer-area
      // banner) to match startChat/sendMessage, not the session-level `error` channel. Return to
      // 'ready' so the user can retry the stop.
      get()._applyLifecycle({ kind: 'optimistic', sessionId: chatSessionId, action: 'clearError' });
      set({ sendError: resolveApiErrorMessage(e, "Failed to cancel session") });
    }
  },

  stopManagedRun: async (chatSessionId, reason = "Stopped by user") => {
    try {
      const response = await stopManagedRunRequest(chatSessionId, reason);
      const payload = response?.data || {};
      set((s) => {
        if (s.sessionDetail?.chatSessionId !== chatSessionId) return {};
        return {
          sessionDetail: {
            ...s.sessionDetail,
            managedRunStatus: payload.managedRunStatus || "STOPPED",
          },
        };
      });
      get().refreshSessionInList(chatSessionId);
      return payload;
    } catch (e) {
      set({ error: resolveApiErrorMessage(e, "Failed to stop mission") });
      return null;
    }
  },
  };
}
